/**
 * Optimizer Control Center — provider / mode / status chips for a row
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { ProviderBadge as UiProviderBadge, Tooltip } from '@/components/ui';
import type { FlowOptimizerProvider } from '@/features/flow-optimizer/services/flow-optimizer-api';
import type { OptimizerRow, OptimizerRowStatus } from '@/features/optimizer-center/optimizer-center.types';
import { rowStatus } from '@/features/optimizer-center/utils/row-status';
import { cn } from '@/lib/utils';

const CHIP =
  'inline-flex items-center rounded-full px-1.5 py-px text-[10px] font-medium whitespace-nowrap';

// The shared pill badge (brand colours: Voluum = pink, Binom = emerald) — the
// bank tabs render the same one (MD-1024), so the look can't drift.
export const ProviderBadge: FC<{ provider: FlowOptimizerProvider }> = ({ provider }) => (
  <UiProviderBadge provider={provider} pill noun="campaign" />
);

const MODE_HELP: Record<OptimizerRow['mode'], string> = {
  CUSTOM:
    'TrafficPulse Optimisation — your scoring formula and exclusion rules decide each item’s weight.',
  STANDARD:
    'Provider default — TrafficPulse isn’t optimising this flow; weights stay as set in the tracker.',
};

export const ModeChip: FC<{ mode: OptimizerRow['mode'] }> = ({ mode }) => (
  <Tooltip content={MODE_HELP[mode]} side="bottom">
    <span
      className={cn(
        CHIP,
        mode === 'CUSTOM' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
      )}
    >
      {mode === 'CUSTOM' ? 'Custom' : 'Standard'}
    </span>
  </Tooltip>
);

const STATUS_STYLES: Record<OptimizerRowStatus, string> = {
  auto: 'bg-success/15 text-success',
  manual: 'bg-muted text-muted-foreground',
  paused: 'bg-warning/15 text-warning',
  error: 'bg-destructive/15 text-destructive',
  standard: 'bg-muted text-muted-foreground',
};

function statusLabel(status: OptimizerRowStatus): string {
  if (status === 'auto') return 'Auto';
  if (status === 'manual') return 'Manual';
  if (status === 'paused') return 'Paused';
  if (status === 'error') return 'Error';
  return 'Standard';
}

function statusHelp(status: OptimizerRowStatus, lastError?: string | null): string {
  if (status === 'auto')
    return 'Auto-run on — the scheduler pushes optimised weights automatically on the set interval.';
  if (status === 'manual')
    return 'Manual — Optimisation is on but auto-run is off. Use “Update now” to push weights yourself.';
  if (status === 'paused')
    return 'Paused — weight pushes are held by the kill switch (emergency brake). Preview & formulas still work.';
  if (status === 'error')
    return lastError ? `Last run failed — ${lastError}` : 'Last run failed.';
  return MODE_HELP.STANDARD;
}

export const StatusChip: FC<{ row: OptimizerRow }> = ({ row }) => {
  const status = rowStatus(row);
  return (
    <Tooltip content={statusHelp(status, row.lastError)} side="bottom">
      <span data-testid="status-chip" className={cn(CHIP, STATUS_STYLES[status])}>
        {statusLabel(status)}
      </span>
    </Tooltip>
  );
};
